import React, {useState} from 'react';
import {Avatar, Table, Tag, Typography} from 'antd';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from '../../../hooks/useTranslation';
import {LossEvent} from '../../../types/events';
import {colors} from '../../../theme/tokens';

const {Text} = Typography;

const AVATAR_PALETTE = [
  '#4e6ef2', '#e05c8a', '#3fba74', '#f5a623',
  '#9b59b6', '#1abc9c', '#e67e22', '#e74c3c',
];

const getAvatarColor = (name: string): string => {
  const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

const severityConfig: Record<string, {color: string; bg: string; border: string; key: string}> = {
  TIER_1: {color: '#b82d14', bg: '#fceae8', border: '#fbd2cd', key: 'events.severity.critical'},
  TIER_2: {color: '#bf5d02', bg: '#fff7df', border: '#ffe4ab', key: 'events.severity.high'},
  TIER_3: {color: '#8a6d00', bg: '#fff7df', border: '#ffe4ab', key: 'events.severity.medium'},
  TIER_4: {color: '#387d54', bg: '#e9fae6', border: '#defed9', key: 'events.severity.low'},
};

const formatCurrency = (val?: number): string => {
  if (val == null) return '—';
  return `€${val.toLocaleString('de-DE')}`;
};

const getInitials = (name?: string): string => {
  if (!name) return '';
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
};

interface EventGridProps {
  dataSource: LossEvent[];
  loading: boolean;
  onTableChange?: (pagination: any) => void;
  pagination?: {current: number; pageSize: number; total: number};
}

const EventGrid: React.FC<EventGridProps> = ({dataSource, loading, onTableChange, pagination}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const columns = [
    {
      title: t('events.columns.id'),
      dataIndex: 'externalCode',
      key: 'id',
      width: 130,
      render: (val: string) => (
        <Text style={{color: colors.primary, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap'}}>
          {val}
        </Text>
      ),
    },
    {
      title: t('events.columns.date'),
      dataIndex: 'occurrenceDate',
      key: 'date',
      width: 110,
    },
    {
      title: t('events.columns.description'),
      dataIndex: 'shortDescription',
      key: 'description',
      ellipsis: {showTitle: false},
    },
    {
      title: t('events.columns.type'),
      dataIndex: 'lossEventType',
      key: 'type',
      width: 160,
      render: (val?: string) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: t('events.columns.businessLine'),
      dataIndex: 'businessLine',
      key: 'businessLine',
      width: 130,
      render: (val?: string) => val
        ? <Text style={{color: colors.textSecondary}}>{val}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: t('events.columns.severity'),
      dataIndex: 'severityTier',
      key: 'severity',
      width: 110,
      render: (val?: string) => {
        if (!val) return <Text type="secondary">—</Text>;
        const cfg = severityConfig[val];
        if (!cfg) return <Tag>{val}</Tag>;
        return (
          <Tag style={{
            color: cfg.color,
            background: cfg.bg,
            borderColor: cfg.border,
            margin: 0,
            fontWeight: 500,
          }}>
            {t(cfg.key)}
          </Tag>
        );
      },
    },
    {
      title: t('events.columns.grossLoss'),
      dataIndex: 'grossLoss',
      key: 'grossLoss',
      width: 120,
      align: 'right' as const,
      render: (val?: number) => <Text>{formatCurrency(val)}</Text>,
    },
    {
      title: t('events.columns.netLoss'),
      dataIndex: 'netLoss',
      key: 'netLoss',
      width: 110,
      align: 'right' as const,
      render: (val?: number) => (
        <Text style={{color: val && val > 0 ? colors.error : 'inherit'}}>
          {formatCurrency(val)}
        </Text>
      ),
    },
    {
      title: t('events.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status?: LossEvent['status']) =>
        status ? (
          <Tag style={{borderColor: status.colorHex, color: status.colorHex, margin: 0}}>
            {status.name}
          </Tag>
        ) : <Text type="secondary">—</Text>,
    },
    {
      title: t('events.columns.owner'),
      dataIndex: 'owner',
      key: 'owner',
      width: 140,
      render: (owner?: LossEvent['owner']) =>
        owner ? (
          <span style={{display: 'flex', alignItems: 'center', gap: 6}}>
            <Avatar
              size="small"
              style={{backgroundColor: getAvatarColor(owner.name), flexShrink: 0, fontSize: 11}}
            >
              {getInitials(owner.name)}
            </Avatar>
            <Text ellipsis>{owner.name.split(' ')[0]}</Text>
          </span>
        ) : <Text type="secondary">—</Text>,
    },
  ];

  return (
    <Table
      rowSelection={{
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
      }}
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      rowKey="id"
      size="small"
      pagination={pagination ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total) => `${total.toLocaleString()} eventos`,
        position: ['bottomRight'],
      } : false}
      scroll={{y: 'calc(100vh - 460px)'}}
      onChange={onTableChange}
      onRow={(record) => ({
        onDoubleClick: () => navigate(`/events/${record.id}`),
        style: {cursor: 'pointer'},
      })}
    />
  );
};

export default EventGrid;
export type {EventGridProps};
